import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { PictureAsPdf } from '@mui/icons-material'
import { Box, Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { Info } from '@ui/feedback'
import { Form2, SubmitButton } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { SectionHeader } from '@ui/structure'
import { updatePlan } from '@mutations'
import FormFields from './components/FormFields'

interface Props {
  selectedPlan: Plan | null
  onUpdatePlan: (plan: Plan) => void
  getCurrentDocument: () => Promise<Blob | null>
  enableSave?: boolean
}

const PlanInformationSection = ({
  selectedPlan,
  onUpdatePlan,
  getCurrentDocument,
  enableSave = true,
}: Props) => {
  const { projectId } = useParams()

  const { enqueueSnackbar } = useSnackbar()

  const defaultValues = useMemo(() => {
    return selectedPlan as Plan
  }, [selectedPlan])

  const { mutate, isLoading } = useMutation({
    mutationFn: async (data: Plan) => {
      const blob = await getCurrentDocument()

      return updatePlan.request(projectId as string, {
        guid: data.guid,
        title: data.node_name,
        comment: data.comment,
        tags: data.tags,
        file: blob,
      })
    },
    onSuccess: (data: Plan) => {
      onUpdatePlan(data)
      enqueueSnackbar('Planinformationen erfolgreich gespeichert', {
        variant: 'success',
      })
    },
    onError: () => {
      enqueueSnackbar('Fehler beim Speichern der Planinformationen', {
        variant: 'error',
      })
    },
  })

  if (!selectedPlan) {
    return (
      <Stack direction="column" spacing={1} sx={{ height: '100%' }}>
        <Box>
          <SectionHeader>Planinformationen</SectionHeader>
        </Box>

        <Stack spacing={1} p={2} sx={{ overflowY: 'auto' }}>
          <Info icon={<PictureAsPdf />}>Kein Plan ausgewählt</Info>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack direction="column" spacing={1} sx={{ height: '100%' }}>
      <Box>
        <SectionHeader>Planinformationen</SectionHeader>
      </Box>

      <Stack spacing={1} p={1} sx={{ overflowY: 'auto' }}>
        <Form2<Plan>
          key={selectedPlan.guid}
          onSubmit={data => mutate(data)}
          defaultValues={defaultValues}
          resetIsDirtyAfterSubmit
        >
          <Stack spacing={1}>
            <FormFields onSubmit={data => mutate(data as Plan)} />

            <Box display="flex" justifyContent="flex-end" flex={1}>
              <SubmitButton loading={isLoading} fullWidth={false} disableIfNotDirty={enableSave} />
            </Box>
          </Stack>
        </Form2>
      </Stack>
    </Stack>
  )
}

export default PlanInformationSection
